import { indexReturnPct } from './signalAnalysisCalcs';
import { sliceSeries } from './txRangeStats';

// All Transactions → Analysis switch: the trade sheet read as PERIODS, so
// scrolling it you feel which stretches were exciting, average, poor or
// tough (Apal 2026-10-17: "exciting areas, average and poor or tough ones
// should be clearly separated, the idea is scroll through the page, and feel
// like we truly have concern in a certain period or not" … "if 4% made and 3%
// loss then that's still a win, but it's not exciting" … "are we making more
// and are there going to be phase of losing or not making, how long that
// period would be").
//
// 1. Cut the trades into runs: a winning streak and the losses right after it.
//    A run lasts until the next run starts — time sitting in cash counts.
// 2. Rate each run against what THIS index gives — its own buy-&-hold
//    yearly return over the same history (Apal 2026-10-17: "it must be
//    dynamic based on what index is giving … We have 18 indexes"). Bitcoin is
//    held to Bitcoin's bar, FTSE to FTSE's; the bar never drops under 5% a
//    year, so a slow index can't turn 3% a year into "Good":
//      Exceptional  2× the bar or more a year
//      Good         the bar or more a year — or made visible money and beat
//                   the index over the same dates
//      Average      made a little: under the bar and not beating the index
//      Poor         lost money
//    Without the index's prices the bar falls back to 10% a year.
//    "Group by" a target instead (Apal 2026-10-22: "why can't we put a filter
//    like per year what type of trades you wanna group?"): the bar is YOUR
//    number a year, exactly — Exceptional 2× it, Good at least it, Average
//    under it, Poor lost money. Beating the index no longer lifts a stretch
//    to Good: against a target the question is "did it make the target".
// 3. Merge neighbouring runs with the same rating into one period. A period
//    shorter than 3 months folds into its neighbour (a 2-month blip between
//    two long stretches is noise when scrolling) — never a Poor one with real
//    losses (at least 2 typical losses).
//
// Pure functions over the page's trade pairs (txRangeStats.buildPairs): the
// sheet's own numbers are never touched. Equity compounds trade by trade.

export const PERIOD_GRADES = { exceptional: 'Exceptional', good: 'Good', average: 'Average', poor: 'Poor' };
export const BAR_FLOOR = 5;            // % a year — the bar never drops below this
export const BAR_FALLBACK = 10;        // % a year — when the index's prices aren't loaded
export const EXCEPTIONAL_MULT = 2;     // × the bar
export const REAL_LOSS_MULT = 2;   // × typical loss — a Poor period this deep never folds away
export const MIN_PERIOD_MONTHS = 3;

const DAY = 86400000;
const round1 = (x) => Math.round(x * 10) / 10;
const ms = (iso) => Date.parse(`${iso}T00:00:00Z`);
const monthsBetween = (a, b) => (ms(b) - ms(a)) / DAY / 30.44;
const yearsBetween = (a, b) => Math.max((ms(b) - ms(a)) / DAY / 365.25, 1 / 12);
const growth = (trades) => trades.reduce((m, t) => m * (1 + t.gain / 100), 1);

/** Closed trades with a result, oldest exit first. */
export const closedTrades = (pairs) => (pairs || [])
  .filter(p => !p.is_open && p.gain != null && p.exit_date && p.entry_date)
  .sort((a, b) => a.exit_date.localeCompare(b.exit_date) || ((a.trade_number || 0) - (b.trade_number || 0)));

/**
 * Rate a stretch: { net (%), years, indexRet (% or null) } against `bar`
 * (% a year — what the index gives); typicalLoss (%) sets what "visible
 * money" means.
 */
export const gradeOf = ({ net, years, indexRet = null }, typicalLoss, bar = BAR_FALLBACK, { target = false } = {}) => {
  if (net < 0) return 'poor';
  const yearly = (Math.pow(1 + net / 100, 1 / years) - 1) * 100;
  if (yearly >= EXCEPTIONAL_MULT * bar) return 'exceptional';
  if (yearly >= bar) return 'good';
  if (target) return 'average';                 // a target is met or it isn't
  // beating the index counts when the money is VISIBLE — at least a typical
  // loss's worth (flat while the index slipped is not a good period)
  // …or when it clearly beat the index — 10+ points (DAX Sep 2007 – Feb 2009:
  // +2% while the index fell 43% is a good period, not an average one)
  if (indexRet != null && net > 0 && (net >= typicalLoss ? net > indexRet : net - indexRet >= 10)) return 'good';
  return 'average';
};

/** What the index gives a year (buy & hold) between two dates, floored. */
export const indexBar = (priceSeries, from, to) => {
  const pts = sliceSeries(priceSeries, from, to);
  if (!pts || pts.length < 2 || !(pts[0].close > 0)) return BAR_FALLBACK;
  const yearly = (Math.pow(pts[pts.length - 1].close / pts[0].close, 1 / yearsBetween(pts[0].date, pts[pts.length - 1].date)) - 1) * 100;
  return Math.max(BAR_FLOOR, yearly);
};

/** "Group by" choices: null = the index's own pace; a number = a target, % a year. */
export const GROUP_TARGETS = [5, 10, 15, 20, 25, 30];
export const parseTarget = (v) => {
  const n = v == null || v === '' || v === 'index' ? null : Number(v);
  return n != null && GROUP_TARGETS.includes(n) ? n : null;
};

/**
 * priceSeries ([[date, close], …], optional): the index's closes, for the
 * buy-&-hold return over each period's dates — the same calc as the page's
 * Index stat (Apal 2026-10-17: "or comparing with index?").
 * target (% a year, optional): group against this instead of the index's pace.
 */
export const computePeriods = (pairs, priceSeries = null, { minMonths = MIN_PERIOD_MONTHS, target = null } = {}) => {
  const tr = closedTrades(pairs);
  const empty = { periods: [], pace: 0, bar: target ?? BAR_FALLBACK, target, typicalLoss: 0, time: { exceptional: 0, good: 0, average: 0, poor: 0 }, longestHard: null };
  if (!tr.length) return empty;
  const lossGains = tr.filter(t => t.gain <= 0).map(t => t.gain);
  const typicalLoss = lossGains.length ? Math.abs(lossGains.reduce((s, g) => s + g, 0) / lossGains.length) : 0;
  const pace = (Math.pow(growth(tr), 1 / yearsBetween(tr[0].entry_date, tr[tr.length - 1].exit_date)) - 1) * 100;
  const bar = target != null ? target
    : priceSeries ? indexBar(priceSeries, tr[0].entry_date, tr[tr.length - 1].exit_date) : BAR_FALLBACK;

  const realLoss = (p) => Math.abs((growth(p.trades.filter(t => t.gain <= 0)) - 1) * 100) >= REAL_LOSS_MULT * typicalLoss;
  const keep = (p) => p.grade === 'poor' && realLoss(p);   // never folded away
  const grade = (p) => gradeOf({
    net: (growth(p.trades) - 1) * 100,
    years: yearsBetween(p.from, p.to),
    indexRet: priceSeries ? indexReturnPct(sliceSeries(priceSeries, p.from, p.to)) : null,
  }, typicalLoss, bar, { target: target != null });

  // 1. runs, each lasting until the next one starts
  const runs = [];
  let cur = [], seenLoss = false;
  tr.forEach(t => {
    if (t.gain > 0 && seenLoss) { runs.push(cur); cur = []; seenLoss = false; }
    cur.push(t); seenLoss = seenLoss || t.gain <= 0;
  });
  runs.push(cur);
  let periods = runs.map((r, i) => ({
    trades: r, from: r[0].entry_date, to: i + 1 < runs.length ? runs[i + 1][0].entry_date : r[r.length - 1].exit_date,
  }));
  periods.forEach(p => { p.grade = grade(p); });

  // 2. merge same-rating neighbours; fold sub-3-month periods (never a Poor one with real losses)
  const mergeSame = (ps) => ps.reduce((out, p) => {
    const last = out[out.length - 1];
    if (last && last.grade === p.grade) { last.trades = [...last.trades, ...p.trades]; last.to = p.to; } else out.push({ ...p });
    return out;
  }, []);
  periods = mergeSame(periods);
  for (let guard = 0; guard < 1000; guard++) {
    const i = periods.findIndex(p => !p.stuck && !keep(p) && monthsBetween(p.from, p.to) < minMonths);
    if (i < 0 || periods.length < 2) break;
    const j = i > 0 && !keep(periods[i - 1]) ? i - 1
      : (i + 1 < periods.length && !keep(periods[i + 1]) ? i + 1 : -1);
    if (j < 0) { periods[i].stuck = true; continue; }       // between two kept Poor periods: it stays
    const [a, b] = i < j ? [i, j] : [j, i];
    const q = { trades: [...periods[a].trades, ...periods[b].trades], from: periods[a].from, to: periods[b].to };
    q.grade = grade(q);
    periods = mergeSame([...periods.slice(0, a), q, ...periods.slice(b + 1)]);
  }

  const time = { exceptional: 0, good: 0, average: 0, poor: 0 };
  const out = periods.map(p => {
    const net = (growth(p.trades) - 1) * 100;
    const m = monthsBetween(p.from, p.to);
    time[p.grade] += m;
    const wins = p.trades.filter(t => t.gain > 0).length;
    return {
      grade: p.grade, from: p.from, to: p.to, months: Math.max(1, Math.round(m)),
      trades: p.trades, wins, losses: p.trades.length - wins, net: round1(net),
      indexRet: priceSeries ? indexReturnPct(sliceSeries(priceSeries, p.from, p.to)) : null,
      pace: round1((Math.pow(1 + net / 100, 1 / yearsBetween(p.from, p.to)) - 1) * 100),
    };
  }).map(p => ({
    ...p,
    // the band's words follow the grade's rule: a win over the index only
    // counts with visible money; "behind" when it trails by 10+ points
    beatIndex: p.indexRet != null && p.net >= typicalLoss && p.net > p.indexRet,
    behindIndex: p.indexRet != null && p.indexRet - p.net >= 10,
  }));
  Object.keys(time).forEach(k => { time[k] = Math.round(time[k]); });
  // the answer to "am I going to face a long stretch of nothing?" — Average or Poor
  const longestHard = out.filter(p => p.grade === 'average' || p.grade === 'poor')
    .reduce((m, p) => (!m || p.months > m.months ? p : m), null);
  return { periods: out, pace: round1(pace), bar: round1(bar), target, typicalLoss: round1(typicalLoss), time, longestHard };
};

/** "5 yrs 7 mos", "17 months", "1 month". */
export const durationText = (m) => {
  if (m < 24) return `${m} month${m === 1 ? '' : 's'}`;
  const y = Math.floor(m / 12), r = m % 12;
  return r ? `${y} yrs ${r} mos` : `${y} yrs`;
};
